import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

// `href` es lo que ve el navegador —clic central, "abrir en pestaña nueva",
// rastreadores—; `target` es lo que recibe onNavigate. Nosotros es el único que
// no es un ancla: tiene página propia.
const navLinks = [
  { label: 'Nosotros', href: '/nosotros', target: 'nosotros' },
  { label: 'Servicios', href: '#servicios', target: 'servicios' },
  { label: 'Conocimiento', href: '#conocimiento', target: 'conocimiento' },
  { label: 'Contacto', href: '#contacto', target: 'contacto' },
];

// La referencia recibe el `lenisRef` y hace el scrollTo aquí dentro. Aquí eso
// vive en App: desde la vista de artículo hay que volver a la home antes de
// desplazarse, y esa decisión no es del header.
export default function Header({ onNavigate }) {
  const headerRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const trigger = ScrollTrigger.create({
      trigger: document.body,
      start: 'top -80',
      onUpdate: (self) => {
        setScrolled(self.progress > 0);
      },
    });
    return () => { trigger.kill(); };
  }, []);

  const handleNavClick = (e, target) => {
    e.preventDefault();
    setMobileOpen(false);
    onNavigate(target);
  };

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-[#0f1419]/95 backdrop-blur-xl shadow-[0_1px_0_rgba(201,162,110,0.08)]'
            : 'bg-transparent'
        }`}
        style={{ padding: scrolled ? '16px 40px' : '24px 40px' }}
      >
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center no-underline" onClick={(e) => {
            e.preventDefault();
            setMobileOpen(false);
            onNavigate('home');
          }}>
            <img
              src="/assets/todd-logo.png"
              alt="Todd Empresas de Familia"
              className={`transition-all duration-500 ${scrolled ? 'h-10' : 'h-12'}`}
            />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <a
                key={link.target}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.target)}
                className="relative font-body text-[13px] uppercase tracking-[0.08em] text-[#b8b0a3] hover:text-[#C9A26E] transition-colors duration-400 group"
              >
                {link.label}
                <span className="absolute left-0 -bottom-1.5 h-px w-0 bg-[#C9A26E] transition-all duration-400 ease-out group-hover:w-full" />
              </a>
            ))}
            <a
              href="#contacto"
              onClick={(e) => handleNavClick(e, 'contacto')}
              className="font-body text-[12px] uppercase tracking-[0.08em] text-[#C9A26E] border border-[#C9A26E]/40 rounded-sm px-6 py-2.5 hover:bg-[#C9A26E] hover:text-[#0f1419] transition-all duration-400"
            >
              Área Clientes
            </a>
          </nav>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden flex flex-col gap-1.5 p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
            aria-expanded={mobileOpen}
          >
            <span className={`w-6 h-[1.5px] bg-[#C9A26E] transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-[4.5px]' : ''}`} />
            <span className={`w-6 h-[1.5px] bg-[#C9A26E] transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-[1.5px]' : ''}`} />
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-40 bg-[#0f1419] flex flex-col items-center justify-center gap-10 transition-all duration-600 md:hidden ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {navLinks.map((link, i) => (
          <a
            key={link.target}
            href={link.href}
            onClick={(e) => handleNavClick(e, link.target)}
            className="font-heading text-3xl text-[#e8e2d9] hover:text-[#C9A26E] transition-colors duration-300 tracking-wide"
            style={{
              opacity: mobileOpen ? 1 : 0,
              transform: mobileOpen ? 'translateY(0)' : 'translateY(30px)',
              transition: `all 0.5s cubic-bezier(0.19, 1, 0.22, 1) ${i * 80 + 200}ms`,
            }}
          >
            {link.label}
          </a>
        ))}
        <a
          href="#contacto"
          onClick={(e) => handleNavClick(e, 'contacto')}
          className="font-heading text-3xl text-[#C9A26E]"
          style={{
            opacity: mobileOpen ? 1 : 0,
            transform: mobileOpen ? 'translateY(0)' : 'translateY(30px)',
            transition: `all 0.5s cubic-bezier(0.19, 1, 0.22, 1) ${navLinks.length * 80 + 200}ms`,
          }}
        >
          Área Clientes
        </a>
      </div>
    </>
  );
}
